/**
 * Firebase Configuration - OnTimeLog 프로젝트 투입시간 관리 시스템
 * FCM (Firebase Cloud Messaging) 초기화 및 설정
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase 설정 (환경 변수에서 가져옴)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Firebase 설정 검증
const isFirebaseConfigured = () => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId
  );
};

// Firebase 앱 초기화 (설정이 있을 때만)
let app = null;
if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig);
  } catch (error) {
    console.warn('[Firebase] Firebase 초기화 실패 (설정 오류일 수 있음):', error.message);
    app = null;
  }
} else {
  console.warn('[Firebase] Firebase 설정이 완료되지 않았습니다. 환경 변수를 확인하세요.');
}

// VAPID 키 (환경 변수에서 가져옴)
export const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || null;

// Messaging 인스턴스 (지원 여부 확인 후 초기화)
let messaging = null;

// FCM 지원 여부 확인 및 Messaging 초기화
export const initializeMessaging = async () => {
  // Firebase 설정이 없으면 초기화하지 않음
  if (!app || !isFirebaseConfigured()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Firebase] Firebase 설정이 없어 FCM을 초기화하지 않습니다.');
    }
    return null;
  }

  try {
    const isSupportedBrowser = await isSupported();
    if (isSupportedBrowser && 'serviceWorker' in navigator) {
      messaging = getMessaging(app);
      return messaging;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Firebase] FCM is not supported in this browser');
      }
      return null;
    }
  } catch (error) {
    console.error('[Firebase] Error initializing messaging:', error);
    return null;
  }
};

// Messaging 인스턴스 가져오기
export const getMessagingInstance = () => {
  return messaging;
};

// FCM 토큰 발급
export const getFCMToken = async (messagingInstance) => {
  if (!messagingInstance) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Firebase] Messaging not initialized');
    }
    return null;
  }

  if (!VAPID_KEY) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Firebase] VAPID key not set. Firebase 환경 변수를 확인하세요.');
    }
    return null;
  }

  try {
    const token = await getToken(messagingInstance, { vapidKey: VAPID_KEY });
    if (token) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Firebase] FCM Token obtained:', token.substring(0, 20) + '...');
      }
      return token;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Firebase] No registration token available');
      }
      return null;
    }
  } catch (error) {
    console.error('[Firebase] Error getting FCM token:', error);
    return null;
  }
};

// 포그라운드 메시지 수신 핸들러
export const setupForegroundMessageHandler = (messagingInstance, callback) => {
  if (!messagingInstance) {
    console.warn('[Firebase] Messaging not initialized');
    return;
  }

  onMessage(messagingInstance, (payload) => {
    console.log('[Firebase] Message received in foreground:', payload);
    if (callback) {
      callback(payload);
    }
  });
};

export { app, isFirebaseConfigured };
export default firebaseConfig;
